import { useRef, useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MapIcon from '@mui/icons-material/Map';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from "react-i18next";
import CartWitnessingContent from '../carts/CartWitnessingContent';
import { HomeTabs } from './HomeTabs';
import { Badge } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import FieldServiceReportDialog from '../publishers/dialogs/FieldServiceReportDialog';
import ScheduleContent from '../schedule/ScheduleContent';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import MyTerritoriesContent, { MyTerritoriesRef } from '../territories/my/MyTerritoriesContent';
import moment from 'moment';
import DisabledFeature from './DisabledFeature';

const HomePage = observer(() => {
    const { panelStore, userStore, publisherStore, reportStore, alertStore, configurationStore } = useStores();
    const { t } = useTranslation();
    const territoriesRef = useRef<MyTerritoriesRef>(null);
    const [tab, setTab] = useState(panelStore.homePage.tab);
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const navigate = useNavigate();
    const user = userStore.userData;
    const hasPublisher = !!user.publisherId;
    const isCartWitnessingFeatureEnabled = configurationStore.isCartWitnessingFeatureEnabled();
    const isTerritoryFeatureEnabled = configurationStore.isTerritoryFeatureEnabled();
    const isTerritoryRequestEnabled = configurationStore.isTerritoryRequestEnabled();

    if (hasPublisher) {
        useEffect(() => {
            const date = moment().subtract(1, 'month').format("YYYY-MM-DD");
            reportStore.shouldPublisherSubmitReport(user.publisherId!, date);
        }, [reportStore.serviceReports[user.publisherId!]]);
    }

    const handleTabChange = (tab: number) => {
        setTab(tab);
        panelStore.setHomePageTab(tab);
    }

    const navigateToNotifications = () => {
        navigate('/alerts');
    }

    const navigateToEvents = () => {
        navigate('/events');
    }

    const navigateToTerritoryRequest = () => {
        navigate('/territory-requests');
    }

    const refreshAssignments = () => {
        territoriesRef.current?.refreshAssignments();
    };

    const headerItems = [];
    if (hasPublisher) {
        const number = alertStore.alerts.length;
        headerItems.push(
            <CalendarMonthIcon key="events_btn" style={{ marginRight: "5px" }} onClick={navigateToEvents} />,
            <Badge key="notification_btn" badgeContent={number} color="error">
                <NotificationsNoneIcon onClick={navigateToNotifications} />
            </Badge>
        );

        if (tab === 1 && isTerritoryRequestEnabled) {
            headerItems.shift();
            headerItems.unshift(
                <MapIcon key="territory_request_btn" style={{ marginRight: "5px" }} onClick={navigateToTerritoryRequest} />
            );
            headerItems.unshift(
                <AutorenewIcon key="territory_refresh_btn" style={{ marginRight: "5px" }} onClick={refreshAssignments} />
            );
        }
    }

    return (
        <Panel title={t("home")} headerItems={headerItems}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <HomeTabs
                    selected={tab}
                    onClick={handleTabChange}
                    alreadySubmittedReport={reportStore.submitted}
                    onSubmitReportClick={() => openDialog("submit-report")}
                />

                <div role="tabpanel" hidden={tab !== 0}>
                    {isCartWitnessingFeatureEnabled ? <CartWitnessingContent /> : <DisabledFeature />}
                </div>
                <div role="tabpanel" hidden={tab !== 1}>
                    {isTerritoryFeatureEnabled ? <MyTerritoriesContent ref={territoriesRef} size="xl" /> : <DisabledFeature />}
                </div>
                <div role="tabpanel" hidden={tab !== 2}>
                    <ScheduleContent editable={false} />
                </div>
            </Container>
            <FieldServiceReportDialog
                isOpen={isOpen("submit-report")}
                publisher={publisherStore.myPublisher!}
                onClose={closeDialog} />
        </Panel>
    );
});

export default HomePage;